import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChartComponent, Series, ValueAxis } from '@progress/kendo-angular-charts';
import {
  CalendarView,
  DateInputsModule,
} from '@progress/kendo-angular-dateinputs';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';

import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import _moment from 'moment';
import { ApiService } from '../service/api.service';

import { Moment } from 'moment';
import { FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomainManagerService } from '../service/domain-manager.service';
import { dueDayLabel } from '../common/payment-due';
import { amountCell, dateCell, ExcelSheetColumn, fileDate, saveExcelSheet } from '../common/excel-sheet';

/** A row of the payment widgets - see GetPaymentDashboard in the API. */
interface PaymentDue {
  CompanyId: number;
  ClientName: string;
  DoctorName: string;
  PlanName: string;
  IsYearly: boolean;
  IsAutoRenew: boolean;
  PaymentDue: number | null;
  LastPaidAmount: number | null;
  DueDate: string;
  CurrencySymbol: string;
}

/** A row of the inactive paid clients widget - see GetInactivePaidClients in the API. */
interface InactivePaidClient {
  CompanyId: number;
  ClientName: string;
  DoctorName: string;
  PlanName: string;
  IsYearly: boolean;
  /** null when the client has never logged in. */
  LastLogin: string | null;
  DaysInactive: number | null;
}

/** The Client Name cell of a widget's export: the name, with plan and billing cycle below it as in the widget. */
function clientNameCell(row: { ClientName: string; DoctorName: string; PlanName: string; IsYearly: boolean }, extra = '') {
  const meta = `${row.PlanName} · ${row.IsYearly ? 'Yearly' : 'Monthly'}${extra}`;
  return { value: `${row.ClientName || row.DoctorName}\n${meta}`, wrap: true };
}

/** The payment widgets' columns, for their Excel export. */
const PAYMENT_EXPORT_COLUMNS: ExcelSheetColumn<PaymentDue>[] = [
  { title: 'Client Name', width: 320, cell: r => clientNameCell(r, r.IsAutoRenew ? ' · AutoPay' : '') },
  { title: 'Payment Due', width: 110, cell: r => amountCell(r.PaymentDue, r.CurrencySymbol) },
  { title: 'Last Paid Amount', width: 130, cell: r => amountCell(r.LastPaidAmount, r.CurrencySymbol) },
  { title: 'Date Due', width: 110, cell: r => dateCell(r.DueDate) },
];

/** The inactive paid clients widget's columns, for its Excel export. */
const INACTIVE_CLIENT_EXPORT_COLUMNS: ExcelSheetColumn<InactivePaidClient>[] = [
  { title: 'Client Name', width: 320, cell: r => clientNameCell(r) },
  { title: 'Plan', width: 130, cell: r => ({ value: r.PlanName }) },
  { title: 'Last Login Date', width: 120, cell: r => dateCell(r.LastLogin, 'Never') },
  {
    title: 'Days Inactive', width: 110,
    cell: r => r.DaysInactive != null ? { value: r.DaysInactive, format: '0 "days"' } : { value: '-' },
  },
];

/** Upcoming payments look this many days ahead, missed payments this many days back. */
const PAYMENT_WINDOW_DAYS = 7;

// const moment = _rollupMoment || _moment;

const moment = _moment;

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

const LOGIN_RANGE_PRESETS = [
  { days: 1, label: 'Today' },
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
];

const DEVICE_LABELS: Record<string, string> = { Ios: 'iOS' };

const DEVICE_COLORS: Record<string, string> = {
  Desktop: '#044b4f',
  Mobile: '#f9978a',
  Tablet: '#779ea0',
  Web: '#2a9d8f',
  Android: '#f4a261',
  Ios: '#264653',
  PWA: '#e9c46a',
};

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.css'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    standalone: false
})
export class MainComponent implements OnInit {
  public value: Date = new Date();
  seriesData: any[] = [];
  seriesData2: any[] = [];
  seriesData3: any[] = [];
  // Daily logged-in users: quick ranges + custom date range (never later than today)
  readonly loginRangePresets = LOGIN_RANGE_PRESETS;
  loginMaxDate = MainComponent.toDateValue(new Date());
  loginActivePreset: number | null = 7;
  loginStartDate = MainComponent.toDateValue(MainComponent.daysAgo(6));
  loginEndDate = this.loginMaxDate;
  // Date objects for the shared app-date-picker (the string fields above are what the API gets)
  loginMaxPickerDate = new Date();
  loginStartPickerDate: Date = MainComponent.daysAgo(6);
  loginEndPickerDate: Date = new Date();
  /** Minimum horizontal space per day so date labels never overlap; wider ranges scroll. */
  readonly loginPointWidth = 48;
  readonly loginChartHeight = 360;
  @ViewChild('loginChart') loginChart?: ChartComponent;
  @ViewChild('loginChartScroll') loginChartScroll?: ElementRef<HTMLElement>;
  loginDayCategories: string[] = [];
  loginAxisTitle = 'Date';
  loginTotalUsers = 0;
  loginTotalLogins = 0;
  loginUsersData: number[] = [];
  loginTotalsData: number[] = [];
  loginMajorUnit: number | undefined = 1;

  readonly paymentWindowDays = PAYMENT_WINDOW_DAYS;
  readonly dueDayLabel = dueDayLabel;
  upcomingPayments: PaymentDue[] = [];
  missedPayments: PaymentDue[] = [];
  paymentsLoading = false;
  /** Paid clients with no login in the last `paymentWindowDays` days. */
  inactivePaidClients: InactivePaidClient[] = [];
  inactiveClientsLoading = false;

  deviceSeries: { name: string; color: string; data: number[] }[] = [];
  deviceSelectedYear = new Date().getFullYear();
  deviceDate = new FormControl(moment());
  allAdminData: any = [];

  month: any;
  count: any;
  Sort: any;

  p: number = 1;
  total: number = 0;
  clientsCounts: any;
  myForm: any;

  CreatedOn: Date = new Date();
  eventSelectedDt = this.value.getFullYear();

  ctrlValue: any;
  item: any;

  //Chart Format
  public format: FormatSettings = {
    displayFormat: 'yyyy',
    inputFormat: 'yyyy',
  };

  public valueAxes: ValueAxis[] = [
    {
      title: { text: 'Clients' },
      min: 0,
      max: null,
    },
  ];

  public series1: Series[] = [
    {
      data: [this.seriesData],
      stack: true,
      type: 'column',
      color: '#044b4f',
    },
  ];
  public series2: Series[] = [
    {
      data: [this.seriesData2],
      stack: true,
      type: 'column',
      color: '#f9978a',
    },
  ];
  public series3: Series[] = [
    {
      data: [this.seriesData3],
      stack: true,
      type: 'column',
      color: '#779ea0',
    },
  ];

  public categories: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  IsDoctor: boolean = false;
  IsTest: boolean = false;
  isRadiologyDomain: boolean = false;

  public crossingValues: number[] = [0, 0, 10, 10];

  constructor(private readonly _http: ApiService, private readonly domainService: DomainManagerService) { }

  ngOnInit() {
    this.getAllData();
    this.getPaymentDashboard();
    this.getInactivePaidClients();
    this.getDeviceTypeCount();
    this.getDailyLoggedInUsers();
    this.IsTest = this.domainService.isTestDomain;
    this.isRadiologyDomain = this.domainService.isRadiologyDomain;
    this.IsDoctor = false;
    this._http.GetActiveAndNonActiveCountPhysio().subscribe((res: any) => {
      this.clientsCounts = res.Data;
      });
  }

  getPaymentDashboard(): void {
    this.paymentsLoading = true;
    this._http.getPaymentDashboard(this.paymentWindowDays).subscribe({
      next: (res: any) => {
        this.upcomingPayments = res?.Success ? res.Data?.Upcoming ?? [] : [];
        this.missedPayments = res?.Success ? res.Data?.Missed ?? [] : [];
        this.paymentsLoading = false;
      },
      error: () => {
        this.upcomingPayments = [];
        this.missedPayments = [];
        this.paymentsLoading = false;
      },
    });
  }

  getInactivePaidClients(): void {
    this.inactiveClientsLoading = true;
    this._http.getInactivePaidClients(this.paymentWindowDays).subscribe({
      next: (res: any) => {
        this.inactivePaidClients = res?.Success ? res.Data ?? [] : [];
        this.inactiveClientsLoading = false;
      },
      error: () => {
        this.inactivePaidClients = [];
        this.inactiveClientsLoading = false;
      },
    });
  }

  /** Saves every row of a payment widget, with the widget's columns, as Upcoming_Payments_16-10-2026.xlsx and so on. */
  exportPayments(rows: PaymentDue[], fileTitle: string): void {
    if (!rows.length) return;
    saveExcelSheet(`${fileTitle}_${fileDate()}.xlsx`, PAYMENT_EXPORT_COLUMNS, rows);
  }

  exportInactivePaidClients(): void {
    if (!this.inactivePaidClients.length) return;
    saveExcelSheet(`Inactive_Paid_Clients_${fileDate()}.xlsx`, INACTIVE_CLIENT_EXPORT_COLUMNS, this.inactivePaidClients);
  }

  getDeviceTypeCount() {
    this._http.GetDeviceCountByMonth(this.deviceSelectedYear).subscribe((res: any) => {
      if (res?.Success) {
        const rows: any[] = res.Data ?? [];
        this.deviceSeries = rows.map((row) => ({
          name: DEVICE_LABELS[row.DeviceType] ?? row.DeviceType,
          color: DEVICE_COLORS[row.DeviceType] ?? '#999999',
          data: this.categories.map((_, i) => row.Counts?.[i] ?? 0),
        }));
      }
    });
  }

  static toDateValue(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** Parses a yyyy-MM-dd value as a local date (new Date('yyyy-MM-dd') would be UTC). */
  static fromDateValue(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  static daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  }

  /** Quick range ending today; `days` = 1 means today only. */
  selectLoginPreset(days: number): void {
    this.loginActivePreset = days;
    this.loginStartDate = MainComponent.toDateValue(MainComponent.daysAgo(days - 1));
    this.loginEndDate = this.loginMaxDate;
    this.syncLoginPickerDates();
    this.getDailyLoggedInUsers();
  }

  private syncLoginPickerDates(): void {
    this.loginStartPickerDate = MainComponent.fromDateValue(this.loginStartDate);
    this.loginEndPickerDate = MainComponent.fromDateValue(this.loginEndDate);
  }

  onLoginDateChange(which: 'start' | 'end', picked: Date | null): void {
    const value = picked ? MainComponent.toDateValue(picked) : '';
    // Ignore a cleared picker and any date later than today
    if (!value || value > this.loginMaxDate) {
      this.syncLoginPickerDates();
      return;
    }
    if (which === 'start') {
      this.loginStartDate = value;
      if (this.loginEndDate < value) {
        this.loginEndDate = value;
      }
    } else {
      this.loginEndDate = value;
      if (this.loginStartDate > value) {
        this.loginStartDate = value;
      }
    }
    this.syncLoginPickerDates();
    this.loginActivePreset = null;
    this.getDailyLoggedInUsers();
  }

  private formatLoginPointLabel(value: string, hourly: boolean): string {
    const date = new Date(value);
    return hourly
      ? date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })
      : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  getDailyLoggedInUsers(): void {
    this._http.GetDailyLoggedInUsers(this.loginStartDate, this.loginEndDate).subscribe((res: any) => {
      if (res?.Success) {
        const rows: any[] = res.Data?.Points ?? [];
        this.loginTotalUsers = res.Data?.TotalUniqueUsers ?? 0;
        this.loginTotalLogins = res.Data?.TotalLogins ?? 0;
        // A single-day range comes back hour by hour, so label points by time instead of date
        const hourly = this.loginStartDate === this.loginEndDate;
        this.loginAxisTitle = hourly ? 'Time' : 'Date';
        this.loginDayCategories = rows.map((row) => this.formatLoginPointLabel(row.Date, hourly));
        this.loginUsersData = rows.map((row) => row.UniqueUsers ?? 0);
        this.loginTotalsData = rows.map((row) => row.TotalLogins ?? 0);
        // Whole-number ticks while counts are small; let Kendo pick the step once they grow
        const maxCount = Math.max(0, ...this.loginTotalsData, ...this.loginUsersData);
        this.loginMajorUnit = maxCount <= 10 ? 1 : undefined;
        // The chart only re-measures on window resize, so redraw once its new width is applied
        setTimeout(() => {
          this.loginChart?.resize();
          // Wide ranges open on the most recent days, which are the ones people check first
          const scroller = this.loginChartScroll?.nativeElement;
          if (scroller) {
            scroller.scrollLeft = scroller.scrollWidth;
          }
        });
      }
    });
  }

  deviceYearChosenHandler(normalizedYear: Moment, dp: any) {
    const ctrlValue = this.deviceDate.value ?? moment();
    ctrlValue.year(normalizedYear.year());
    this.deviceDate.setValue(ctrlValue);
    dp.close();
    this.deviceSelectedYear = normalizedYear.year();
    this.getDeviceTypeCount();
  }

  getAllData() {
    let selectedYear = this.eventSelectedDt;
    this._http
      .getAppointmentAndPatentCountByMonth(selectedYear)
      .subscribe((res: any) => {
        this.allAdminData = res.Data;

        let maxVal = 0; // initialize max value to zero
        // loop through all objects in the array
        if (!!this.allAdminData) {
          for (let index = 0; index < this.allAdminData.length; index++) {
            this.categories.forEach((prop) => {
              if (this.allAdminData[index][prop] > maxVal) {
                maxVal = this.allAdminData[index][prop];
              }
            });
          }
        }


        maxVal = Math.ceil(maxVal / 1000) * 1000;


        let dynamicvalue = maxVal + 1000
        if (maxVal > dynamicvalue) {
          var temp = maxVal.toString().length;
          var counter = 1;
          for (let index = 0; index < temp - 1; index++) {
            counter = counter * (10);
          }
          maxVal = maxVal + counter;
          this.valueAxes.find((t) => (t.max = maxVal));
        }


        // var temp = maxVal.toString().length;
        // var counter = 1;
        // for (let index = 0; index < temp-1; index++) {
        //   counter = counter * (10);
        // }
        // maxVal = maxVal + counter;
        // this.valueAxes.find((t) => (t.max = maxVal));





        this.seriesData = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        this.seriesData2 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        this.seriesData3 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];



        if (this.allAdminData) {
          for (let i = 0; i < this.allAdminData.length; i++) {
            let selectedYear = this.eventSelectedDt;
            let year = this.allAdminData[i].Year;
            if (selectedYear.toString() == year.toString()) {
              if (this.allAdminData[i].Jan > 0) {
                if (i == 0)
                  this.seriesData[0] =
                    this.seriesData[0] + this.allAdminData[i].Jan;
                if (i == 1)
                  this.seriesData2[0] =
                    this.seriesData2[0] + this.allAdminData[i].Jan;
                if (i == 2)
                  this.seriesData3[0] =
                    this.seriesData3[0] + this.allAdminData[i].Jan;
              }
              if (this.allAdminData[i].Feb > 0) {
                if (i == 0)
                  this.seriesData[1] =
                    this.seriesData[1] + this.allAdminData[i].Feb;
                if (i == 1)
                  this.seriesData2[1] =
                    this.seriesData2[1] + this.allAdminData[i].Feb;
                if (i == 2)
                  this.seriesData3[1] =
                    this.seriesData3[1] + this.allAdminData[i].Feb;
              }
              if (this.allAdminData[i].Mar > 0) {
                if (i == 0)
                  this.seriesData[2] =
                    this.seriesData[2] + this.allAdminData[i].Mar;
                if (i == 1)
                  this.seriesData2[2] =
                    this.seriesData2[2] + this.allAdminData[i].Mar;
                if (i == 2)
                  this.seriesData3[2] =
                    this.seriesData3[2] + this.allAdminData[i].Mar;
              }
              if (this.allAdminData[i].Apr > 0) {
                if (i == 0)
                  this.seriesData[3] =
                    this.seriesData[3] + this.allAdminData[i].Apr;
                if (i == 1)
                  this.seriesData2[3] =
                    this.seriesData2[3] + this.allAdminData[i].Apr;
                if (i == 2)
                  this.seriesData3[3] =
                    this.seriesData3[3] + this.allAdminData[i].Apr;
              }
              if (this.allAdminData[i].May > 0) {
                if (i == 0)
                  this.seriesData[4] =
                    this.seriesData[4] + this.allAdminData[i].May;
                if (i == 1)
                  this.seriesData2[4] =
                    this.seriesData2[4] + this.allAdminData[i].May;
                if (i == 2)
                  this.seriesData3[4] =
                    this.seriesData3[4] + this.allAdminData[i].May;
              }
              if (this.allAdminData[i].Jun > 0) {
                if (i == 0)
                  this.seriesData[5] =
                    this.seriesData[5] + this.allAdminData[i].Jun;
                if (i == 1)
                  this.seriesData2[5] =
                    this.seriesData2[5] + this.allAdminData[i].Jun;
                if (i == 2)
                  this.seriesData3[5] =
                    this.seriesData3[5] + this.allAdminData[i].Jun;
              }
              if (this.allAdminData[i].Jul > 0) {
                if (i == 0)
                  this.seriesData[6] =
                    this.seriesData[6] + this.allAdminData[i].Jul;
                if (i == 1)
                  this.seriesData2[6] =
                    this.seriesData2[6] + this.allAdminData[i].Jul;
                if (i == 2)
                  this.seriesData3[6] =
                    this.seriesData3[6] + this.allAdminData[i].Jul;
              }
              if (this.allAdminData[i].Aug > 0) {
                if (i == 0)
                  this.seriesData[7] =
                    this.seriesData[7] + this.allAdminData[i].Aug;
                if (i == 1)
                  this.seriesData2[7] =
                    this.seriesData2[7] + this.allAdminData[i].Aug;
                if (i == 2)
                  this.seriesData3[7] =
                    this.seriesData3[7] + this.allAdminData[i].Aug;
              }
              if (this.allAdminData[i].Sep > 0) {
                if (i == 0)
                  this.seriesData[8] =
                    this.seriesData[8] + this.allAdminData[i].Sep;
                if (i == 1)
                  this.seriesData2[8] =
                    this.seriesData2[8] + this.allAdminData[i].Sep;
                if (i == 2)
                  this.seriesData3[8] =
                    this.seriesData3[8] + this.allAdminData[i].Sep;
              }
              if (this.allAdminData[i].Oct > 0) {
                if (i == 0)
                  this.seriesData[9] =
                    this.seriesData[9] + this.allAdminData[i].Oct;
                if (i == 1)
                  this.seriesData2[9] =
                    this.seriesData2[9] + this.allAdminData[i].Oct;
                if (i == 2)
                  this.seriesData3[9] =
                    this.seriesData3[9] + this.allAdminData[i].Oct;
              }
              if (this.allAdminData[i].Nov > 0) {
                if (i == 0)
                  this.seriesData[10] =
                    this.seriesData[10] + this.allAdminData[i].Nov;
                if (i == 1)
                  this.seriesData2[10] =
                    this.seriesData2[10] + this.allAdminData[i].Nov;
                if (i == 2)
                  this.seriesData3[10] =
                    this.seriesData3[10] + this.allAdminData[i].Nov;
              }
              if (this.allAdminData[i].Dec > 0) {
                if (i == 0)
                  this.seriesData[11] =
                    this.seriesData[11] + this.allAdminData[i].Dec;
                if (i == 1)
                  this.seriesData2[11] =
                    this.seriesData2[11] + this.allAdminData[i].Dec;
                if (i == 2)
                  this.seriesData3[11] =
                    this.seriesData3[11] + this.allAdminData[i].Dec;
              }
            }
          }

          this.Sort = this.allAdminData.sort().reverse((Id: any) => Id);
        }

      });

  }

  pageChangeEvent(event: number) {
    this.p = event;
    this.getAllData();
  }
  //Mat-datepicker year Start Here
  date = new FormControl(moment());
  chosenYearHandler(normalizedYear: Moment, dp: any) {
    let ctrlValue: any = null;
    ctrlValue = this.date.value;
    ctrlValue.year(normalizedYear.year());
    this.date.setValue(ctrlValue);
    dp.close();
    this.eventSelectedDt = ctrlValue._d.getFullYear();
    this.getAllData();
  }

  //End Here
}
